import { useEffect, useState } from 'react';
import EditorialLayout from '@/components/editorial/EditorialLayout';
import { RagMetricsService, RegionStats } from '@/services/ragMetricsService';
import { MetricsOverview } from '@/components/rag-metrics/MetricsOverview';
import { RegionStatsTable } from '@/components/rag-metrics/RegionStatsTable';
import { TimeSeriesChart } from '@/components/rag-metrics/TimeSeriesChart';
import { RejectedDomainsChart } from '@/components/rag-metrics/RejectedDomainsChart';
import { AlertsTable } from '@/components/rag-metrics/AlertsTable';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';

export default function RagMetricsPage() {
  const [timeRange, setTimeRange] = useState<number>(30);
  const [loading, setLoading] = useState(true);
  const [regionStats, setRegionStats] = useState<RegionStats[]>([]);
  const [timeSeriesData, setTimeSeriesData] = useState<any[]>([]);
  const [rejectedDomains, setRejectedDomains] = useState<any[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<any[]>([]);
  const { toast } = useToast();

  const loadMetrics = async () => {
    try {
      setLoading(true);
      const [stats, timeSeries, rejected, alerts] = await Promise.all([
        RagMetricsService.getRegionStats(timeRange),
        RagMetricsService.getTimeSeriesData(timeRange),
        RagMetricsService.getRejectedDomainsStats(timeRange),
        RagMetricsService.getRecentAlerts(20),
      ]);
      
      setRegionStats(stats);
      setTimeSeriesData(timeSeries);
      setRejectedDomains(rejected);
      setRecentAlerts(alerts);
    } catch (error) {
      console.error('Failed to load metrics:', error);
      toast({
        title: 'Feil ved lasting av metrics',
        description: 'Kunne ikke laste RAG-statistikk',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadMetrics();
  }, [timeRange]);

  const totalRuns = regionStats.reduce((sum, r) => sum + r.totalRuns, 0);
  const avgSuccessRate = regionStats.length > 0
    ? regionStats.reduce((sum, r) => sum + r.successRate, 0) / regionStats.length
    : 0;
  const avgSourcesFound = regionStats.length > 0
    ? regionStats.reduce((sum, r) => sum + r.avgSourcesFound, 0) / regionStats.length
    : 0;
  const avgExecutionTime = regionStats.length > 0
    ? regionStats.reduce((sum, r) => sum + r.avgExecutionTime, 0) / regionStats.length
    : 0;

  return (
    <EditorialLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold">RAG Metrics Dashboard</h1>
            <p className="text-muted-foreground">
              Statistikk for RAG-generering av Briefr Bullets kontinentartikler
            </p>
          </div>
          <Select value={timeRange.toString()} onValueChange={(v) => setTimeRange(Number(v))}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Siste 7 dager</SelectItem>
              <SelectItem value="30">Siste 30 dager</SelectItem>
              <SelectItem value="90">Siste 90 dager</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12">Laster metrics...</div>
        ) : (
          <Tabs defaultValue="overview" className="space-y-6">
            <TabsList>
              <TabsTrigger value="overview">Oversikt</TabsTrigger>
              <TabsTrigger value="regions">Regioner</TabsTrigger>
              <TabsTrigger value="timeline">Tidslinje</TabsTrigger>
              <TabsTrigger value="rejected">Avviste domener</TabsTrigger>
              <TabsTrigger value="alerts">Varsler</TabsTrigger>
            </TabsList>

            <TabsContent value="overview" className="space-y-6">
              <MetricsOverview
                totalRuns={totalRuns}
                successRate={avgSuccessRate}
                avgSourcesFound={avgSourcesFound}
                avgExecutionTime={avgExecutionTime}
              />
              <RegionStatsTable stats={regionStats} />
            </TabsContent>

            <TabsContent value="regions" className="space-y-6">
              <RegionStatsTable stats={regionStats} />
            </TabsContent>

            <TabsContent value="timeline" className="space-y-6">
              <TimeSeriesChart data={timeSeriesData} />
            </TabsContent>

            <TabsContent value="rejected" className="space-y-6">
              <RejectedDomainsChart data={rejectedDomains} />
            </TabsContent>

            <TabsContent value="alerts" className="space-y-6">
              <AlertsTable alerts={recentAlerts} />
            </TabsContent>
          </Tabs>
        )}
      </div>
    </EditorialLayout>
  );
}
